// @ts-nocheck
import { useMemo } from 'react';

import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import NumberUtils from '@/utils/number.utils';

import './styles/LEsOwnershipCard.css';

function translate(key, fallback) {
  if (typeof window !== 'undefined') {
    const resolver = window.SuarteI18n?.t || window.t;
    if (typeof resolver === 'function') {
      const result = resolver(key);
      if (typeof result === 'string' && result.trim().length > 0) {
        return result;
      }
    }
  }
  return fallback;
}

function maskWalletIfNeeded(value) {
  if (typeof value !== 'string') {
    return value ?? '—';
  }

  const trimmed = value.trim();
  if (trimmed.startsWith('0x') && trimmed.length > 10) {
    return `${trimmed.slice(0, 4)}…${trimmed.slice(-4)}`;
  }

  return trimmed;
}

function resolveOwnerDisplay(params) {
  const {
    copyOwner,
    fallbackOwnerName,
    isOwnerView,
    viewerDisplayName,
  } = params;

  if (isOwnerView) {
    if (viewerDisplayName && viewerDisplayName.trim().length > 0) {
      return viewerDisplayName;
    }
    return translate('limitedEdition.you', 'You');
  }

  if (!copyOwner) {
    return fallbackOwnerName || '—';
  }

  if (typeof copyOwner === 'string') {
    return maskWalletIfNeeded(copyOwner) || fallbackOwnerName || '—';
  }

  const candidate =
    copyOwner.displayName
    ?? copyOwner.publicHandle
    ?? copyOwner.handle
    ?? copyOwner.username
    ?? copyOwner.userName
    ?? copyOwner.user_username
    ?? copyOwner.wallet
    ?? copyOwner.walletAddress
    ?? copyOwner.masked
    ?? null;

  if (candidate) {
    return maskWalletIfNeeded(candidate);
  }

  if (copyOwner.walletAddress) {
    return maskWalletIfNeeded(copyOwner.walletAddress);
  }

  return fallbackOwnerName || '—';
}

function resolveOwnerImageId(owner, fallbackImage) {
  if (owner && typeof owner === 'object') {
    const candidate =
      owner.user_image?.image_id
      ?? owner.image?.image_id
      ?? owner.profile_image?.image_id
      ?? owner.image_id
      ?? owner.imageId
      ?? owner.user_image_id
      ?? owner.image
      ?? null;
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }

  if (typeof fallbackImage === 'string' && fallbackImage.trim().length > 0) {
    return fallbackImage;
  }

  return null;
}

function resolveOwnerProfileType(owner, fallbackType) {
  if (owner && typeof owner === 'object') {
    const candidate =
      owner.type
      ?? owner.user_type
      ?? owner.profileType
      ?? owner.__t
      ?? null;
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.toLowerCase();
    }
  }

  if (typeof fallbackType === 'string' && fallbackType.trim().length > 0) {
    return fallbackType.toLowerCase();
  }

  return 'collector';
}

function formatSaleDate(value) {
  if (!value && value !== 0) {
    return '—';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear() % 100;
  return `${day}/${month}/${year.toString().padStart(2, '0')}`;
}

function formatPrice(value, currency) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const numeric = typeof value === 'string' ? Number(value) : value;
  const currencyCode = typeof currency === 'string' && currency.trim()
    ? currency.toUpperCase()
    : 'EUR';

  if (Number.isFinite(numeric)) {
    if (Number.isInteger(numeric)) {
      try {
        return NumberUtils.getArtworkPrice(numeric, currencyCode);
      } catch (error) {
        // ignore and fallback to Intl
      }
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: numeric % 1 === 0 ? 0 : 2,
      }).format(numeric);
    } catch (error) {
      return `${numeric.toLocaleString()} ${currencyCode}`.trim();
    }
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  return '—';
}

export default function LEsOwnershipCard({
  copy,
  fallbackEditionNumber = null,
  fallbackOwnerName = null,
  fallbackOwnerImage = null,
  fallbackOwnerType = null,
  fallbackPrice = null,
  badgeLabelOverride = null,
  loading = false,
  isOwnerView = false,
  viewerDisplayName = null,
  fallbackSoldAt = null,
}) {
  // Short-circuit to fallback data when no copy is available to avoid extra loading states.
  const resolvedCopy = copy ?? null;

  const title = useMemo(
    () => translate('limitedEditionOwnership.title', 'Limited Edition'),
    [],
  );
  const ownerLabel = useMemo(
    () => translate('limitedEditionOwnership.ownerLabel', 'LE Owner'),
    [],
  );
  const dateLabel = useMemo(
    () => translate('limitedEditionOwnership.date', 'Date'),
    [],
  );
  const priceLabel = useMemo(
    () => translate('limitedEditionOwnership.priceLabel', 'Price'),
    [],
  );

  const editionNumber = resolvedCopy?.editionNumber ?? fallbackEditionNumber;
  const priceValue = resolvedCopy?.lastPurchasePrice ?? fallbackPrice?.amount ?? null;
  const priceCurrency = resolvedCopy?.currency ?? fallbackPrice?.currency ?? null;
  const soldAt = resolvedCopy?.soldAt ?? fallbackSoldAt ?? null;

  const ownerDisplay = useMemo(
    () => resolveOwnerDisplay({
      copyOwner: resolvedCopy?.currentOwner,
      fallbackOwnerName,
      isOwnerView,
      viewerDisplayName,
    }),
    [resolvedCopy?.currentOwner, fallbackOwnerName, isOwnerView, viewerDisplayName],
  );

  const ownerImageId = useMemo(
    () => resolveOwnerImageId(
      resolvedCopy?.currentOwner ?? resolvedCopy?.owner ?? null,
      fallbackOwnerImage,
    ),
    [resolvedCopy?.currentOwner, resolvedCopy?.owner, fallbackOwnerImage],
  );

  const ownerProfileType = useMemo(
    () => resolveOwnerProfileType(
      resolvedCopy?.currentOwner ?? resolvedCopy?.owner ?? null,
      fallbackOwnerType,
    ),
    [resolvedCopy?.currentOwner, resolvedCopy?.owner, fallbackOwnerType],
  );

  const priceDisplay = useMemo(
    () => formatPrice(priceValue, priceCurrency),
    [priceCurrency, priceValue],
  );

  const formattedDate = useMemo(
    () => formatSaleDate(soldAt),
    [soldAt],
  );

  const badgeLabel = useMemo(() => {
    if (badgeLabelOverride && `${badgeLabelOverride}`.toString().trim().length > 0) {
      return badgeLabelOverride;
    }
    if (editionNumber === null || editionNumber === undefined || editionNumber === '') {
      return null;
    }
    return `${editionNumber}`;
  }, [badgeLabelOverride, editionNumber]);

  return (
    <section
      className={`le-ownership-card${loading ? ' le-ownership-card--loading' : ''}`}
      aria-label={title}
    >
      <header className="le-ownership-card__header">
        <div className="le-ownership-card__title-block">
          <span className="le-ownership-card__title">{title}</span>
        </div>
        {badgeLabel ? <span className="le-ownership-card__badge">{badgeLabel}</span> : null}
      </header>

      <dl className="le-ownership-card__list">
        <div className="le-ownership-card__row le-ownership-card__row--head">
          <dt>{dateLabel}</dt>
          <dt>{ownerLabel}</dt>
          <dt>{priceLabel}</dt>
        </div>
        <div className="le-ownership-card__row le-ownership-card__row--two">
          <dd className="le-ownership-card__value le-ownership-card__value--date">{formattedDate}</dd>
          <dd className="le-ownership-card__value le-ownership-card__value--owner">
            <span className="le-ownership-card__owner">
              <UserProfileImage
                image={ownerImageId}
                typeOfProfile={ownerProfileType}
                className="le-ownership-card__owner-image"
              />
              <span className="le-ownership-card__owner-name">{ownerDisplay}</span>
            </span>
          </dd>
          <dd className="le-ownership-card__value le-ownership-card__value--price le-ownership-card__price">{priceDisplay}</dd>
        </div>
      </dl>

      {loading ? (
        <div className="le-ownership-card__skeleton">
          <span />
          <span />
          <span />
        </div>
      ) : null}
    </section>
  );
}
